import {
  createInvitationRequestSchema,
  invitationLink,
  maximumInvitationsPerDeletion,
  requiresPersonLink,
  type CreateInvitationRequest,
  type CreatedInvitationResponse,
  type InvitationEmailDelivery,
  type InvitationView,
  type PermissionBundle,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export const invitationStatusLabelKeys: Readonly<Record<InvitationView['status'], TranslationKey>> =
  {
    pending: 'invitations.status.pending',
    accepted: 'invitations.status.accepted',
    revoked: 'invitations.status.revoked',
    expired: 'invitations.status.expired',
  };

export { invitationLink };

export const invitationDeliveryLabelKeys: Readonly<
  Record<InvitationEmailDelivery, TranslationKey>
> = {
  sent: 'invitations.delivery.sent',
  no_sender: 'invitations.delivery.no_sender',
  unavailable: 'invitations.delivery.unavailable',
  failed: 'invitations.delivery.failed',
};

/** What the inviter sees after creating an invitation: the one-time link and what the e-mail did. */
export interface CreatedInvitationNotice {
  link: string;
  recipientEmail: string;
  emailDelivery: InvitationEmailDelivery;
}

export function createdInvitationNotice(
  origin: string,
  created: CreatedInvitationResponse,
): CreatedInvitationNotice {
  return {
    link: invitationLink(origin, created.token),
    recipientEmail: created.recipientEmail,
    emailDelivery: created.emailDelivery,
  };
}

/**
 * A staff invitation offering every ticked role, in the order the form lists them; null while the
 * address is not valid or no role is ticked.
 */
export function staffInvitationBody(
  email: string,
  ticked: ReadonlySet<PermissionBundle>,
  offered: readonly PermissionBundle[],
): CreateInvitationRequest | null {
  const parsed = createInvitationRequestSchema.safeParse({
    recipientEmail: email.trim(),
    permissionBundles: offered.filter((bundle) => ticked.has(bundle)),
  });
  return parsed.success ? parsed.data : null;
}

/** Only a waiting invitation can still be withdrawn. */
export function canWithdrawInvitation(invitation: InvitationView): boolean {
  return invitation.status === 'pending';
}

/**
 * A finished invitation (accepted, withdrawn or expired) can be deleted from the history. Without
 * the right to offer staff roles, only parent and athlete invitations are the caller's to delete.
 */
export function canDeleteInvitation(
  invitation: InvitationView,
  canOfferStaffRoles: boolean,
): boolean {
  if (invitation.status === 'pending') return false;
  return canOfferStaffRoles || invitation.permissionBundles.every(requiresPersonLink);
}

/** Splits a selection into deletion requests the API accepts, in the order given. */
export function invitationDeletionBatches(invitationIds: readonly string[]): string[][] {
  const batches: string[][] = [];
  for (let start = 0; start < invitationIds.length; start += maximumInvitationsPerDeletion) {
    batches.push(invitationIds.slice(start, start + maximumInvitationsPerDeletion));
  }
  return batches;
}
